import { useState, useEffect, useContext } from "react";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import {
  StyleSheet,
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  Image,
  Platform,
} from "react-native";
import { useAppTheme } from "../../hooks/useAppTheme";
import type { ThemeColors } from "../../utils/constants/colors";

import * as Auth from "../../services/authService";
import oktaConfig from "../../configs/okta";
import { useDispatch, useSelector } from "react-redux";
import { clearSessionStorage } from "../../services/utils/storageService";
import { SimpleLineIcons } from "@expo/vector-icons";
import { persistor } from "../../redux/configureStore";
import { getAllNotificationsCount } from "../../redux/dispatcher/notificationDispatcher";
import { Avatar } from "react-native-paper";
import { useTranslation } from "../../utils/useTranslation";

function CustomDrawerContent(props: any) {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const [mainDrawer, setMainDrawer] = useState(true);
  const [filteredItems, setFilteredItems] = useState<any>([]);
  const { isMobileDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const { userInfo, hasAvatar, profilePhoto, isDrawer } = useSelector(
    (state: any) => state.configurations,
  );
  const { logo } = useSelector((state: any) => state.configurations);
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();

  const { loading, notificationCount } = useSelector(
    (state: any) => state.notification,
  );

  const getFinalDisplayName = () => {
    if (userInfo?.display_name && userInfo.display_name !== "undefined") return userInfo.display_name;
    if (userInfo?.displayName && userInfo.displayName !== "undefined") return userInfo.displayName;
    if (userInfo?.first_name && userInfo.first_name !== "undefined") return `${userInfo.first_name} ${userInfo.last_name || ''}`.trim();
    if (userInfo?.given_name && userInfo.given_name !== "undefined") return userInfo.given_name;
    return "User";
  };

  const finalDisplayName = getFinalDisplayName();
  const initial = finalDisplayName.charAt(0).toUpperCase();

  useEffect(() => {
    dispatch(getAllNotificationsCount());
  }, []);

  const toggleMainDrawer = () => {
    setMainDrawer(true);
    setFilteredItems([]);
  };

  const logoutHandler = () => {
    Auth.customLogout().then(() => {
      clearSessionStorage();
      dispatch({ type: "LOGOUT" });
      window.location.href = `${oktaConfig.api.redirectUri}`;
    });
    persistor.purge();
  };

  const onItemParentPress = (key: any) => {
    const filteredMainDrawerRoutes = props.drawerItems.find((e: any) => {
      return e && e.key === key;
    });
    if (filteredMainDrawerRoutes.routes.length === 1) {
      const selectedRoute = filteredMainDrawerRoutes.routes[0];
      props.navigation.toggleDrawer();
      props.navigation.navigate(selectedRoute.nav, {
        screen: selectedRoute.routeName,
      });
    } else {
      setMainDrawer(false);
      setFilteredItems(filteredMainDrawerRoutes);
    }
  };

  function renderMainDrawer() {
    return (
      <View style={styles.menuList}>
        {props.drawerItems.map(
          (parent: any) =>
            parent && (
              <TouchableOpacity
                key={parent.key}
                testID={parent.key}
                activeOpacity={0.7}
                onPress={() => onItemParentPress(parent.key)}
                style={styles.menuItem}
              >
                <Text style={styles.menuText}>{t(parent.title)}</Text>
                {parent.routes && parent.routes.length > 1 && (
                  <Text style={styles.chevronText}>›</Text>
                )}
              </TouchableOpacity>
            ),
        )}
        {!isSmallDevice && renderLogoutBtn()}
      </View>
    );
  }

  function renderFilteredItemsDrawer() {
    return (
      <View style={styles.menuList}>
        <TouchableOpacity
          onPress={() => toggleMainDrawer()}
          style={styles.backButton}
          activeOpacity={0.7}
        >
          <Text style={styles.backArrow}>‹</Text>
          <Text style={styles.backButtonLabel}>{t("back") || "Back"}</Text>
        </TouchableOpacity>

        <View style={styles.divider} />

        {filteredItems.routes.map((route: any) => (
          <TouchableOpacity
            key={route.routeName}
            testID={route.routeName}
            activeOpacity={0.7}
            onPress={() =>
              props.navigation.navigate(route.nav, {
                screen: route.routeName,
              })
            }
            style={styles.menuItem}
          >
            <Text style={styles.menuText}>{t(route.title)}</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  }

  function renderLogoutBtn() {
    if (Platform.OS === "web") return null;
    return (
      <>
        <View style={styles.divider} />
        <TouchableOpacity
          onPress={() => logoutHandler()}
          testID="customDrawer-logout"
          activeOpacity={0.7}
          style={styles.menuItem}
        >
          <Text style={[styles.menuText, { color: colors.danger }]}>
            Log out
          </Text>
        </TouchableOpacity>
      </>
    );
  }

  function renderBottomSettings() {
    return (
      <View style={styles.accountSection}>
        <View style={styles.accountRow}>
          <View style={styles.avatarContainer}>
            {hasAvatar ? (
              <Avatar.Image
                size={isDrawer ? 40 : 32}
                source={profilePhoto}
                style={{ backgroundColor: colors.surface }}
              />
            ) : (
              <Avatar.Text
                size={isDrawer ? 40 : 32}
                label={initial}
                style={{ backgroundColor: "#3B82F6" }}
              />
            )}
            {!loading && notificationCount > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{notificationCount}</Text>
              </View>
            )}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.accountUserText} numberOfLines={1}>
              {finalDisplayName}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.settingsButton}
            onPress={() => {
              props.navigation.closeDrawer();
              props.navigation.navigate("SettingsM");
            }}
            activeOpacity={0.7}
          >
            <SimpleLineIcons
              name="options"
              size={14}
              color={colors.textSecondary}
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.drawerContainer}>
      <ScrollView
        style={styles.scrollArea}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Navigation items */}
        {mainDrawer ? renderMainDrawer() : renderFilteredItemsDrawer()}
      </ScrollView>

      {/* Bottom user profile (mobile only) */}
      {(isMobileDevice || isSmallDevice) && renderBottomSettings()}
    </View>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    drawerContainer: {
      flex: 1,
      backgroundColor: colors.surface,
    },
    scrollArea: {
      flex: 1,
    },
    scrollContent: {
      paddingBottom: 20,
    },
    logoContainer: {
      alignItems: "center",
      paddingVertical: 20,
    },
    logo: {
      width: 72,
      height: 72,
    },
    divider: {
      height: 1,
      backgroundColor: colors.borderSubtle,
      marginHorizontal: 16,
    },

    // Menu items
    menuList: {
      paddingTop: 16,
      paddingHorizontal: 10,
    },
    menuItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 14,
      paddingHorizontal: 10,
      borderRadius: 8,
      marginBottom: 8,
    },
    menuText: {
      flex: 1,
      fontSize: 13,
      fontWeight: "600",
      color: colors.text,
    },
    chevronText: {
      fontSize: 18,
      color: colors.textSecondary,
      marginLeft: 4,
    },

    // Back button (sub-drawer)
    backButton: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 10,
      paddingHorizontal: 10,
      marginTop: 0,
      marginBottom: 8,
    },
    backArrow: {
      fontSize: 20,
      color: colors.primary,
      fontWeight: "600",
    },
    backButtonLabel: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.primary,
      marginLeft: 4,
    },

    // Bottom account section
    accountSection: {
      borderTopWidth: 1,
      borderTopColor: colors.borderSubtle,
      paddingVertical: 10,
      paddingHorizontal: 12,
    },
    accountRow: {
      flexDirection: "row",
      alignItems: "center",
    },
    avatarContainer: {
      marginRight: 10,
    },
    accountUserText: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.text,
    },
    settingsButton: {
      padding: 8,
      borderRadius: 6,
      backgroundColor: colors.surfaceSubtle,
    },

    // Notification badge
    badge: {
      position: "absolute",
      top: -2,
      right: -4,
      backgroundColor: colors.danger,
      borderRadius: 10,
      paddingHorizontal: 4,
      minWidth: 16,
      height: 16,
      justifyContent: "center",
      alignItems: "center",
      zIndex: 10,
      borderWidth: 1.5,
      borderColor: colors.surface,
    },
    badgeText: {
      color: "white",
      fontSize: 9,
      fontWeight: "700",
      textAlign: "center",
    },
  });
}

export default CustomDrawerContent;
